import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from './cn';
import { useTheme, type Theme } from './useTheme';

const OPTIONS: Array<{ value: Theme; label: string; Icon: typeof Sun }> = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

/**
 * The theme control: three states, all visible at once.
 *
 * A segmented control rather than a cycling button, because with three options
 * a single button forces the user to click through states to find the one they
 * want and never says what the current one is. Here the choice is readable
 * without touching it.
 *
 * "System" is offered explicitly, and is the default. Somebody whose machine
 * switches at sunset expects the console to follow, and a light/dark pair alone
 * silently opts them out of that forever.
 *
 * `compact` drops the visible labels for the console header, where the control
 * sits beside the user's name, Security and Sign out and the words cost more
 * width than they add: the three icons are conventional, the active one is
 * still marked, and the label survives as the tooltip and the accessible name.
 * Wherever there is room — the drawer, the worker profile — leave it off.
 */
export function ThemeToggle({ className, compact = false }: { className?: string; compact?: boolean }) {
  const { theme, setTheme } = useTheme();

  return (
    <div
      className={cn(
        'inline-flex items-center gap-0.5 rounded-btn border border-neutral-200 bg-neutral-100 p-0.5',
        className,
      )}
      role="radiogroup"
      aria-label="Colour theme"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => setTheme(value)}
            title={label}
            className={cn(
              'flex items-center rounded-[6px] text-caption transition-colors duration-hover',
              // 24px at the least, and a thumb's width on a touch screen.
              compact ? 'h-6 min-w-6 justify-center pointer-coarse:h-10 pointer-coarse:min-w-10' : 'min-h-6 gap-1.5 px-2 py-1 pointer-coarse:min-h-10 pointer-coarse:px-3',
              active
                ? 'bg-white text-neutral-900 shadow-card'
                : 'text-neutral-500 hover:text-neutral-800',
            )}
          >
            <Icon aria-hidden className="h-3.5 w-3.5" />
            {/* The label is the accessible name and the thing that makes the
                control legible; hidden on the narrowest screens only, where
                the icons and the title attribute carry it. */}
            {compact ? (
              <span className="sr-only">{label}</span>
            ) : (
              <>
                <span className="hidden sm:inline">{label}</span>
                <span className="sr-only sm:hidden">{label}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
